import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import './PWAInstallBanner.css';

const PWAInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Détecter iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Détecter si l'app est déjà installée (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       (window.navigator.standalone) ||
                       document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Vérifier si la bannière a déjà été fermée
    const bannerDismissed = localStorage.getItem('pwa-install-banner-dismissed');
    
    // Si l'app n'est pas installée et la bannière n'a pas été fermée, afficher
    if (!standalone && !bannerDismissed) {
      setShowBanner(true);
    }

    // Écouter l'événement beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      // Empêcher l'affichage automatique de la bannière du navigateur
      e.preventDefault();
      // Sauvegarder l'événement pour l'utiliser plus tard
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Écouter l'événement appinstalled (quand l'app est installée)
    const handleAppInstalled = () => {
      setShowBanner(false);
      setDeferredPrompt(null);
      localStorage.removeItem('pwa-install-banner-dismissed');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Afficher le prompt d'installation
      deferredPrompt.prompt();
      
      // Attendre la réponse de l'utilisateur
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('✅ Utilisateur a accepté l\'installation PWA');
      } else {
        console.log('❌ Utilisateur a refusé l\'installation PWA');
      }
      
      // Réinitialiser le prompt
      setDeferredPrompt(null);
      setShowBanner(false);
      localStorage.setItem('pwa-install-banner-dismissed', 'true');
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-banner-dismissed', 'true');
  };

  // Ne pas afficher si :
  // - L'app est déjà installée (standalone)
  // - La bannière a été fermée
  // - Pas de prompt disponible et pas iOS
  if (!showBanner || isStandalone) {
    return null;
  }

  // Instructions iOS (Safari)
  if (isIOS && !deferredPrompt) {
    return (
      <div className="pwa-install-banner pwa-install-banner-ios">
        <div className="pwa-install-banner-content">
          <div className="pwa-install-banner-icon">
            <Download size={24} />
          </div>
          <div className="pwa-install-banner-text">
            <div className="pwa-install-banner-title">Installer WorkBiFlow</div>
            <div className="pwa-install-banner-subtitle">
              Appuyez sur <span className="ios-share-icon">📤</span> puis sur <strong>"Sur l'écran d'accueil"</strong>
            </div>
          </div>
        </div>
        <button 
          className="pwa-install-banner-close"
          onClick={handleDismiss}
          aria-label="Fermer"
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  // Bannière standard (Android/Chrome avec prompt)
  return (
    <div className="pwa-install-banner">
      <div className="pwa-install-banner-content">
        <div className="pwa-install-banner-icon">
          <Download size={24} />
        </div>
        <div className="pwa-install-banner-text">
          <div className="pwa-install-banner-title">Installer WorkBiFlow</div>
          <div className="pwa-install-banner-subtitle">
            Installez l'application pour un accès rapide
          </div>
        </div>
      </div>
      <div className="pwa-install-banner-actions">
        <button 
          className="pwa-install-banner-button"
          onClick={handleInstallClick}
        >
          Installer
        </button>
        <button 
          className="pwa-install-banner-close"
          onClick={handleDismiss}
          aria-label="Fermer"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default PWAInstallBanner;

